import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  Directive,
  Input,
  NgModule,
  ViewEncapsulation,
} from '@angular/core';
import {
  CDK_TABLE,
  CDK_TABLE_TEMPLATE,
  CdkCell,
  CdkCellDef,
  CdkCellOutlet,
  CdkColumnDef,
  CdkFooterCell,
  CdkFooterCellDef,
  CdkFooterRow,
  CdkFooterRowDef,
  CdkHeaderCell,
  CdkHeaderCellDef,
  CdkHeaderRow,
  CdkHeaderRowDef,
  CdkNoDataRow,
  CdkRow,
  CdkRowDef,
  CdkTable,
  DataRowOutlet,
  FooterRowOutlet,
  HeaderRowOutlet,
  NoDataRowOutlet,
  STICKY_POSITIONING_LISTENER,
  _COALESCED_STYLE_SCHEDULER,
  _CoalescedStyleScheduler,
} from '@angular/cdk/table';
import {_DisposeViewRepeaterStrategy, _VIEW_REPEATER_STRATEGY, DataSource} from '@angular/cdk/collections';
import {BehaviorSubject, Observable} from 'rxjs';
import {SdSort, Sort} from './sort';
import {SdPaginator} from './paginator';

@Component({
  selector: 'sd-table, table[sd-table]',
  exportAs: 'sdTable',
  template: CDK_TABLE_TEMPLATE,
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [HeaderRowOutlet, DataRowOutlet, NoDataRowOutlet, FooterRowOutlet],
  providers: [
    {provide: CdkTable, useExisting: SdTable},
    {provide: CDK_TABLE, useExisting: SdTable},
    {provide: _COALESCED_STYLE_SCHEDULER, useClass: _CoalescedStyleScheduler},
    {provide: _VIEW_REPEATER_STRATEGY, useClass: _DisposeViewRepeaterStrategy},
    {provide: STICKY_POSITIONING_LISTENER, useValue: null},
  ],
  host: {
    class: 'sd-table sd-table w-full border-collapse text-sm',
    '[class.cdk-table-fixed-layout]': 'fixedLayout',
  },
})
export class SdTable<T> extends CdkTable<T> {
  protected override stickyCssClass = 'sd-table-sticky sticky';
  protected override needsPositionStickyOnElement = false;
}

@Directive({
  selector: '[sdCellDef]',
  standalone: true,
  providers: [{provide: CdkCellDef, useExisting: SdCellDef}],
})
export class SdCellDef extends CdkCellDef {}

@Directive({
  selector: '[sdHeaderCellDef]',
  standalone: true,
  providers: [{provide: CdkHeaderCellDef, useExisting: SdHeaderCellDef}],
})
export class SdHeaderCellDef extends CdkHeaderCellDef {}

@Directive({
  selector: '[sdFooterCellDef]',
  standalone: true,
  providers: [{provide: CdkFooterCellDef, useExisting: SdFooterCellDef}],
})
export class SdFooterCellDef extends CdkFooterCellDef {}

@Directive({
  selector: '[sdColumnDef]',
  standalone: true,
  providers: [
    {provide: CdkColumnDef, useExisting: SdColumnDef},
    {provide: 'SD_SORT_HEADER_COLUMN_DEF', useExisting: SdColumnDef},
  ],
})
export class SdColumnDef extends CdkColumnDef {
  @Input('sdColumnDef')
  override get name(): string {
    return super.name;
  }
  override set name(value: string) {
    this._setNameInput(value);
  }
}

@Directive({
  selector: 'sd-header-cell, th[sd-header-cell]',
  standalone: true,
  host: {
    class: 'sd-header-cell px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-[var(--m365-text-secondary,#6b6b70)] border-b border-[var(--m365-border-rest,#ececf0)]',
    role: 'columnheader',
  },
})
export class SdHeaderCell extends CdkHeaderCell {}

@Directive({
  selector: 'sd-footer-cell, td[sd-footer-cell]',
  standalone: true,
  host: {class: 'sd-footer-cell px-3 py-3 border-t border-[var(--m365-border-rest,#ececf0)]'},
})
export class SdFooterCell extends CdkFooterCell {}

@Directive({
  selector: 'sd-cell, td[sd-cell]',
  standalone: true,
  host: {class: 'sd-cell px-3 py-3 border-b border-[var(--m365-border-rest,#ececf0)] align-middle'},
})
export class SdCell extends CdkCell {}

@Directive({
  selector: '[sdHeaderRowDef]',
  standalone: true,
  providers: [{provide: CdkHeaderRowDef, useExisting: SdHeaderRowDef}],
  inputs: [
    {name: 'columns', alias: 'sdHeaderRowDef'},
    {name: 'sticky', alias: 'sdHeaderRowDefSticky', transform: booleanAttribute},
  ],
})
export class SdHeaderRowDef extends CdkHeaderRowDef {}

@Directive({
  selector: '[sdFooterRowDef]',
  standalone: true,
  providers: [{provide: CdkFooterRowDef, useExisting: SdFooterRowDef}],
  inputs: [
    {name: 'columns', alias: 'sdFooterRowDef'},
    {name: 'sticky', alias: 'sdFooterRowDefSticky', transform: booleanAttribute},
  ],
})
export class SdFooterRowDef extends CdkFooterRowDef {}

@Directive({
  selector: '[sdRowDef]',
  standalone: true,
  providers: [{provide: CdkRowDef, useExisting: SdRowDef}],
  inputs: [
    {name: 'columns', alias: 'sdRowDefColumns'},
    {name: 'when', alias: 'sdRowDefWhen'},
  ],
})
export class SdRowDef<T> extends CdkRowDef<T> {}

@Component({
  selector: 'sd-header-row, tr[sd-header-row]',
  template: '<ng-container cdkCellOutlet></ng-container>',
  standalone: true,
  imports: [CdkCellOutlet],
  host: {class: 'sd-header-row bg-[var(--m365-bg-subtle,#f4f4f7)]', role: 'row'},
})
export class SdHeaderRow extends CdkHeaderRow {}

@Component({
  selector: 'sd-row, tr[sd-row]',
  template: '<ng-container cdkCellOutlet></ng-container>',
  standalone: true,
  imports: [CdkCellOutlet],
  host: {class: 'sd-row hover:bg-black/[0.02]', role: 'row'},
})
export class SdRow extends CdkRow {}

@Component({
  selector: 'sd-footer-row, tr[sd-footer-row]',
  template: '<ng-container cdkCellOutlet></ng-container>',
  standalone: true,
  imports: [CdkCellOutlet],
  host: {class: 'sd-footer-row', role: 'row'},
})
export class SdFooterRow extends CdkFooterRow {}

@Directive({
  selector: 'ng-container[sdNoDataRow]',
  standalone: true,
  providers: [{provide: CdkNoDataRow, useExisting: SdNoDataRow}],
})
export class SdNoDataRow extends CdkNoDataRow {}

export class SdTableDataSource<T> extends DataSource<T> {
  private readonly _data = new BehaviorSubject<T[]>([]);
  private readonly _filter = new BehaviorSubject<string>('');
  private readonly _renderData = new BehaviorSubject<T[]>([]);
  private _sort: SdSort | null = null;
  private _paginator: SdPaginator | null = null;

  constructor(initialData: T[] = []) {
    super();
    this.data = initialData;
  }

  get data(): T[] {
    return this._data.value;
  }
  set data(data: T[]) {
    this._data.next(data || []);
  }

  get filter(): string {
    return this._filter.value;
  }
  set filter(filter: string) {
    this._filter.next(filter ?? '');
  }

  get sort(): SdSort | null {
    return this._sort;
  }
  set sort(sort: SdSort | null) {
    this._sort = sort;
    this._update();
  }

  get paginator(): SdPaginator | null {
    return this._paginator;
  }
  set paginator(paginator: SdPaginator | null) {
    this._paginator = paginator;
    this._update();
  }

  sortingDataAccessor: (data: T, sortHeaderId: string) => string | number = (data, id) => {
    const value = (data as Record<string, unknown>)[id];
    return value == null ? '' : (value as string | number);
  };

  filterPredicate: (data: T, filter: string) => boolean = (data, filter) =>
    JSON.stringify(data).toLowerCase().includes(filter.trim().toLowerCase());

  connect(): Observable<T[]> {
    this._update();
    return this._renderData.asObservable();
  }

  disconnect(): void {}

  private _update(): void {
    const filtered = this.data.filter((row) => this.filterPredicate(row, this.filter));
    const sorted = this._sortData(filtered);
    const paged = this._pageData(sorted);
    this._renderData.next(paged);
  }

  private _sortData(data: T[]): T[] {
    const sort: Sort | undefined = this._sort
      ? {active: this._sort.active, direction: this._sort.direction}
      : undefined;
    if (!sort?.active || !sort.direction) {
      return data;
    }
    return [...data].sort((a, b) => {
      const av = this.sortingDataAccessor(a, sort.active);
      const bv = this.sortingDataAccessor(b, sort.active);
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sort.direction === 'asc' ? cmp : -cmp;
    });
  }

  private _pageData(data: T[]): T[] {
    if (!this._paginator) {
      return data;
    }
    const start = this._paginator.pageIndex * this._paginator.pageSize;
    return data.slice(start, start + this._paginator.pageSize);
  }
}

const TABLE_DIRECTIVES = [
  SdTable,
  SdCellDef,
  SdHeaderCellDef,
  SdFooterCellDef,
  SdColumnDef,
  SdHeaderCell,
  SdFooterCell,
  SdCell,
  SdHeaderRowDef,
  SdFooterRowDef,
  SdRowDef,
  SdHeaderRow,
  SdRow,
  SdFooterRow,
  SdNoDataRow,
];

@NgModule({
  imports: TABLE_DIRECTIVES,
  exports: TABLE_DIRECTIVES,
})
export class SdTableModule {}
